import { defineComponent as _defineComponent } from 'vue'
import { leadStatuses, StatusFiltersValues } from "@/constants/statusFilters";
  import { computed, onMounted, ref, watch } from "vue";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import InputSearch from "@/components/ui/input/InputSearch.vue";
  import CreateLeadModal from "@/components/ui/modals/CreateLeadModal.vue";
  import router from "@/router";
  import store from "@/store";
  import { getLeadStatusClass } from "@/helpers/status";
  import plural from "@/features/plural";
  import { debounce } from "@/helpers/debounce";
  import { LeadInterface } from "@/types/LeadInterface";
  import { POSITION, useToast } from "vue-toastification";
  import { LeadStatus } from "@/enums/LeadStatus";
  import { isValidFileSize, isXlsxType } from "@/helpers/fileUpload";
  import { formatDate } from "@/helpers/date";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import { EstateInterface } from "@/types/EstateInterface";
  import { useRequest } from "@/features/useRequest";
  import { EstateActions } from "@/store/modules/estate";
  import { capitalizeString } from "@/helpers/transformation/string";
  import { isCollaborator } from "@/helpers/estate/isCollaborator";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'Leads',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();
  const filters = leadStatuses;
  const activeFilter = ref(leadStatuses[0]);
  const searchQuery = ref("");
  const debounceSearch = debounce(getLeads);
  const estateId = computed(() => {
    return store.state.user.selectedEstateId;
  });
  const leads = ref<LeadInterface[]>([]);
  const leadList = ref([]);
  const isSelectAll = ref(false);
  const selectedLeadsList = computed(() => {
    return leadList.value.filter((item) => item.selected);
  });
  const newStatus = ref(LeadStatus.NEW);
  const showCreateModal = ref(false);
  const showConfirmBulkDeleteModal = ref(false);
  const isEditStatusMode = ref(false);
  const estate = ref<EstateInterface | null>(null);

  const estateHasCollaborators = computed(() => {
    const collaboratorsLength = estate.value?.collaboratorBusiness?.length;

    return collaboratorsLength && collaboratorsLength > 0;
  });

  const headers = computed(() => {
    const headers = [
      "Name",
      "Email",
      "Phone Number",
      "Lead Status",
      "Source",
      "Postcode",
      "Buyer Type",
      "Created",
    ];

    if (estateHasCollaborators.value) {
      headers.push("Business");
    }

    return headers;
  });

  const isEstateCollaborator = computed(() => {
    if (!estate.value) return false;

    return isCollaborator(estate.value);
  });

  const getEstate = async () => {
    const { handleRequestWithLoading } = useRequest(
      undefined,
      undefined,
      POSITION.TOP_LEFT
    );
    const res = await handleRequestWithLoading<EstateInterface, string>(
      EstateActions.GET_ESTATE,
      estateId.value,
      loading
    );

    estate.value = res?.data;
  };

  const getLeadBusiness = (lead: LeadInterface) => {
    return lead.business?.name ? capitalizeString(lead.business.name) : "-";
  };

  const templateDownloadLink = ref(null);

  function onRow(id: string) {
    router.push(`/leads/${id}`);
  }

  function changeFilter(item: { label: string; value: string }) {
    activeFilter.value = item;
    getLeads();
  }
  onMounted(() => {
    store.dispatch("contact/getUnwatchedLeads", estateId.value);
    getLeads();
    getEstate();
    getTemplateLink();
  });

  const loading = ref(false);

  function getLeads() {
    if (estateId.value) {
      loading.value = true;
      store
        .dispatch("contact/getLeads", {
          estateId: estateId.value,
          query: searchQuery.value,
          status:
            activeFilter.value.value === StatusFiltersValues.ANY
              ? ""
              : activeFilter.value.value,
        })
        .then((res) => {
          leads.value = res.data;
          leadList.value = leads.value.map((item) => {
            return {
              id: item._id,
              selected: false,
              status: item.status,
            };
          });
          isSelectAll.value = false;
        })
        .finally(() => {
          loading.value = false;
        });
    }
  }
  function selectLot(evnt: boolean, index: number) {
    leadList.value[index].selected = !leadList.value[index].selected;
    isSelectAll.value = selectedLeadsList.value.length > 0;
  }

  function selectAll(isSelect: boolean) {
    isSelectAll.value = isSelect;
    leadList.value.forEach((item) => {
      item.selected = isSelect;
    });
  }

  function inviteToWaitlist() {
    store
      .dispatch("subscription/inviteLeadToWaitlist", {
        estateId: estateId.value,
        leadIds: selectedLeadsList.value.map((item) => item.id),
      })
      .then(() => {
        toast.success("Emails were successfully sent");
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  }

  function bulkDeleteLeads() {
    store
      .dispatch("contact/bulkDeleteLeads", {
        estateId: estateId.value,
        leadIds: selectedLeadsList.value.map((item) => item.id),
      })
      .then(() => {
        getLeads();
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  }

  const bulkChangeStatuses = () => {
    store
      .dispatch("contact/bulkChangeLeadsStatuses", {
        estateId: estateId.value,
        leadIds: selectedLeadsList.value.map((item) => item.id),
        status: newStatus.value,
      })
      .then(() => {
        isEditStatusMode.value = false;
        getLeads();
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  };

  function uploadLeads(e: Event) {
    const input = e.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    const selectedFile = input.files[0];

    if (!isXlsxType(selectedFile) || !isValidFileSize(selectedFile)) {
      return;
    }

    const data = new FormData();

    data.append("file", selectedFile as File);
    store
      .dispatch("contact/importLeads", { estateId: estateId.value, data })
      .then(() => {
        toast.success("File successfully uploaded");
        getLeads();
      })
      .catch((err) => {
        if (err.response.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Something went wrong");
        }
      });
  }
  function getTemplateLink() {
    store
      .dispatch("contact/downloadLeadsTemplate")
      .then(
        (res) =>
          (templateDownloadLink.value = res.data.message ? null : res.data)
      );
  }

  watch(
    () => estateId.value,
    () => {
      getLeads();
      getEstate();
    }
  );

  watch(
    () => searchQuery.value,
    () => {
      debounceSearch();
    }
  );
  watch(
    () => selectedLeadsList.value,
    (newValue) => {
      if (!newValue.length) {
        isEditStatusMode.value = false;
      }
    }
  );

const __returned__ = { toast, filters, activeFilter, searchQuery, debounceSearch, estateId, leads, leadList, isSelectAll, selectedLeadsList, newStatus, showCreateModal, showConfirmBulkDeleteModal, isEditStatusMode, estate, estateHasCollaborators, headers, isEstateCollaborator, getEstate, getLeadBusiness, templateDownloadLink, onRow, changeFilter, loading, getLeads, selectLot, selectAll, inviteToWaitlist, bulkDeleteLeads, bulkChangeStatuses, uploadLeads, getTemplateLink, get leadStatuses() { return leadStatuses }, get StatusFiltersValues() { return StatusFiltersValues }, computed, onMounted, ref, watch, CheckboxInput, ConfirmDeleteModal, DropDown, InputSearch, CreateLeadModal, get router() { return router }, get store() { return store }, get getLeadStatusClass() { return getLeadStatusClass }, get plural() { return plural }, get debounce() { return debounce }, get LeadInterface() { return LeadInterface }, get POSITION() { return POSITION }, get useToast() { return useToast }, get LeadStatus() { return LeadStatus }, get isValidFileSize() { return isValidFileSize }, get isXlsxType() { return isXlsxType }, get formatDate() { return formatDate }, get LeadSourceEnum() { return LeadSourceEnum }, get EstateInterface() { return EstateInterface }, get useRequest() { return useRequest }, get EstateActions() { return EstateActions }, get capitalizeString() { return capitalizeString }, get isCollaborator() { return isCollaborator } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})